import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Users, Plus, Search, Eye, Settings, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import type { UserGroup, UserRoleState, PaginationInfo, Permission } from '../../../../types/userRole';
import { mockUserGroups } from '../../../../data/mockUserGroups';
import RippleButton from '../../../common/RippleButton';
import CreateGroupModal from './components/CreateGroupModal';
import ConfigurePermissionsModal from './components/ConfigurePermissionsModal';
import SuccessModal from './components/SuccessModal';
import ViewGroupModal from './components/ViewGroupModal';
import { useCreateGroup } from './hooks/useCreateGroup';
import '../../../../styles/userRoleManagement.css';

interface UserRoleManagementProps {
  className?: string;
  onConfigurePermissions?: (group: UserGroup) => void;
  onCreateGroupPermissions?: (group: UserGroup) => void;
  onPermissionsComplete?: (handler: (permissions: Permission[]) => void) => void;
  onSuccessModalComplete?: () => void;
}

const UserRoleManagement: React.FC<UserRoleManagementProps> = ({
  className = '',
  onConfigurePermissions,
  onCreateGroupPermissions,
  onPermissionsComplete,
  onSuccessModalComplete
}) => {
  const [state, setState] = useState<UserRoleState>({
    groups: mockUserGroups,
    searchTerm: '',
    selectedGroups: [],
    currentPage: 1,
    itemsPerPage: 5,
    totalItems: mockUserGroups.length
  });

  // View Group Modal State
  const [viewGroupModal, setViewGroupModal] = useState<{
    isOpen: boolean;
    group: UserGroup | null;
  }>({ isOpen: false, group: null });

  // Configure Permissions Modal State
  const [configurePermissionsModal, setConfigurePermissionsModal] = useState<{
    isOpen: boolean;
    group: UserGroup | null;
  }>({ isOpen: false, group: null });

  // Stabilize the group created callback
  const handleGroupCreated = useCallback((newGroup: UserGroup) => {
    console.log('UserRoleManagement: Adding new group to table:', newGroup);
    setState(prev => ({
      ...prev,
      groups: [...prev.groups, newGroup],
      totalItems: prev.totalItems + 1
    }));
  }, []);

  // Create group hook
  const createGroup = useCreateGroup({
    existingGroups: state.groups,
    onGroupCreated: handleGroupCreated,
    onConfigurePermissions,
    onCreateGroupPermissions,
    onSuccessModalComplete
  });

  // Expose the external permissions complete function
  useEffect(() => {
    if (onPermissionsComplete) {
      onPermissionsComplete(createGroup.actions.handleExternalPermissionsComplete);
    }
  }, [onPermissionsComplete, createGroup.actions.handleExternalPermissionsComplete]);

  // Filter groups based on search term
  const filteredGroups = useMemo(() => {
    if (!state.searchTerm) return state.groups;
    
    return state.groups.filter(group =>
      group.name.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
      group.groupId.toLowerCase().includes(state.searchTerm.toLowerCase())
    );
  }, [state.groups, state.searchTerm]);

  // Calculate pagination
  const paginationInfo = useMemo((): PaginationInfo => {
    const totalItems = filteredGroups.length;
    const totalPages = Math.ceil(totalItems / state.itemsPerPage);
    const startItem = (state.currentPage - 1) * state.itemsPerPage + 1;
    const endItem = Math.min(state.currentPage * state.itemsPerPage, totalItems);

    return {
      currentPage: state.currentPage,
      totalPages,
      totalItems,
      itemsPerPage: state.itemsPerPage,
      startItem,
      endItem
    };
  }, [filteredGroups.length, state.currentPage, state.itemsPerPage]);

  // Get current page groups
  const currentPageGroups = useMemo(() => {
    const startIndex = (state.currentPage - 1) * state.itemsPerPage;
    const endIndex = startIndex + state.itemsPerPage;
    return filteredGroups.slice(startIndex, endIndex);
  }, [filteredGroups, state.currentPage, state.itemsPerPage]);

  const handleSearch = (searchTerm: string) => {
    setState(prev => ({
      ...prev,
      searchTerm,
      currentPage: 1 // Reset to first page when searching
    }));
  };

  const handleSelectGroup = (groupId: number) => {
    setState(prev => ({
      ...prev,
      selectedGroups: prev.selectedGroups.includes(groupId)
        ? prev.selectedGroups.filter(id => id !== groupId)
        : [...prev.selectedGroups, groupId]
    }));
  };

  const handleSelectAll = () => {
    const allCurrentPageIds = currentPageGroups.map(group => group.id);
    const allSelected = allCurrentPageIds.every(id => state.selectedGroups.includes(id));
    
    setState(prev => ({
      ...prev,
      selectedGroups: allSelected
        ? prev.selectedGroups.filter(id => !allCurrentPageIds.includes(id))
        : [...new Set([...prev.selectedGroups, ...allCurrentPageIds])]
    }));
  };

  const handlePageChange = (page: number) => {
    setState(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  const handleCreateGroup = () => {
    createGroup.actions.startCreateGroup();
  };

  const handleViewGroup = (group: UserGroup) => {
    setViewGroupModal({ isOpen: true, group });
  };

  const handleSaveGroupName = async (groupId: number, newName: string) => {
    // Update the group name in the state
    setState(prev => ({
      ...prev,
      groups: prev.groups.map(group =>
        group.id === groupId
          ? { ...group, name: newName }
          : group
      )
    }));
    
    // Update the modal state to reflect the change
    setViewGroupModal(prev => ({
      ...prev,
      group: prev.group ? { ...prev.group, name: newName } : null
    }));
  };

  const handleGroupSettings = (group: UserGroup) => {
    if (onConfigurePermissions) {
      onConfigurePermissions(group);
    } else {
      setConfigurePermissionsModal({ isOpen: true, group });
    }
  };

  const handleDeleteGroup = (group: UserGroup) => {
    if (confirm(`Are you sure you want to delete the group "${group.name}"?`)) {
      setState(prev => ({
        ...prev,
        groups: prev.groups.filter(g => g.id !== group.id),
        selectedGroups: prev.selectedGroups.filter(id => id !== group.id)
      }));
    }
  };

  return (
    <div className={`user-role-management ${className}`}>
      {/* Header Section */}
      <div className="user-role-header">
        <div className="user-role-header-left">
          <div className="user-role-icon">
            <Users size={24} />
          </div>
          <div className="user-role-title-section">
            <h1 className="user-role-title">Manage User Groups and Permissions</h1>
          </div>
        </div>
        <RippleButton
          className="btn-create-group"
          onClick={handleCreateGroup}
          variant="primary"
        >
          <Plus size={16} />
          Create Group
        </RippleButton>
      </div>

      {/* Search Section */}
      <div className="user-role-search-section">
        <div className="search-input-container">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search groups..."
            value={state.searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Table Section */}
      <div className="user-role-table-container">
        <table className="user-role-table">
          <thead className="user-role-table-header">
            <tr>
              <th className="checkbox-column user-role-checkbox-actions-custom">
                <input
                  type="checkbox"
                  checked={currentPageGroups.length > 0 && currentPageGroups.every(group => state.selectedGroups.includes(group.id))}
                  onChange={handleSelectAll}
                  className="table-checkbox"
                />
              </th>
              <th className="group-details-column">GROUP DETAILS</th>
              <th className="members-column">MEMBERS</th>
              <th className="permissions-column">PERMISSIONS</th>
              <th className="actions-column user-role-checkbox-actions-custom" >ACTIONS</th>
            </tr>
          </thead>
          <tbody className="user-role-table-body">
            {currentPageGroups.map((group) => (
              <tr key={group.id} className="user-role-table-row">
                <td className="checkbox-cell">
                  <input
                    type="checkbox"
                    checked={state.selectedGroups.includes(group.id)}
                    onChange={() => handleSelectGroup(group.id)}
                    className="table-checkbox"
                  />
                </td>
                <td className="group-details-cell">
                  <div className="group-details">
                    <div className="group-info">
                      <div className="group-name">{group.name}</div>
                      <div className="group-id">{group.groupId}</div>
                    </div>
                  </div>
                </td>
                <td className="members-cell">
                  <div className="member-count">
                    <Users size={16} className="member-icon" />
                    <span className="member-number">{group.memberCount}</span>
                  </div>
                </td>
                <td className="permissions-cell">
                  <div className="permission-badges">
                    {group.permissions.map((permission) => (
                      <span
                        key={permission.id}
                        className="permission-badge"
                        style={{ backgroundColor: permission.color }}
                      >
                        {permission.name}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="actions-cell">
                  <div className="action-buttons">
                    <button
                      className="action-btn view-btn"
                      onClick={() => handleViewGroup(group)}
                      title="View Group Details"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      className="action-btn settings-btn"
                      onClick={() => handleGroupSettings(group)}
                      title="Configure Permissions"
                    >
                      <Settings size={16} />
                    </button>
                    <button
                      className="action-btn delete-btn"
                      onClick={() => handleDeleteGroup(group)}
                      title="Delete Group"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Section */}
      <div className="user-role-pagination">
        <div className="pagination-info">
          Showing {paginationInfo.startItem} to {paginationInfo.endItem} of {paginationInfo.totalItems} groups
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(state.currentPage - 1)}
            disabled={state.currentPage === 1}
          >
            <ChevronLeft size={16} />
          </button>
          
          {Array.from({ length: paginationInfo.totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`pagination-number ${page === state.currentPage ? 'active' : ''}`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}
          
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(state.currentPage + 1)}
            disabled={state.currentPage === paginationInfo.totalPages}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Create Group Modals */}
      <CreateGroupModal
        isOpen={createGroup.state.showModals.create}
        onClose={() => createGroup.actions.closeModal('create')}
        onCreateGroup={createGroup.actions.handleCreateGroup}
        existingGroups={createGroup.existingGroupNames}
      />

      <ConfigurePermissionsModal
        isOpen={createGroup.state.showModals.configure}
        groupName={createGroup.state.newGroup.name}
        onSkip={createGroup.actions.handleSkipPermissions}
        onConfigure={createGroup.actions.handleConfigurePermissions}
        onClose={() => createGroup.actions.closeModal('configure')}
      />

      <SuccessModal
        isOpen={createGroup.state.showModals.success}
        groupName={createGroup.state.newGroup.name}
        onComplete={createGroup.actions.handleSuccessComplete}
        hasPermissions={createGroup.state.hasPermissions}
      />

      {/* View Group Modal */}
      <ViewGroupModal
        isOpen={viewGroupModal.isOpen}
        group={viewGroupModal.group}
        onClose={() => setViewGroupModal({ isOpen: false, group: null })}
        onSave={handleSaveGroupName}
      />

      {/* Configure Permissions Modal for Settings */}
      <ConfigurePermissionsModal
        isOpen={configurePermissionsModal.isOpen}
        groupName={configurePermissionsModal.group?.name || ''}
        onSkip={() => setConfigurePermissionsModal({ isOpen: false, group: null })}
        onConfigure={() => {
          // Handle configure permissions logic here
          setConfigurePermissionsModal({ isOpen: false, group: null });
          alert('Configure permissions functionality would be implemented here.');
        }}
        onClose={() => setConfigurePermissionsModal({ isOpen: false, group: null })}
      />
    </div>
  );
};

export default UserRoleManagement;